import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WsSection, FieldStatusChip } from "./shared";
import { t } from "@/lib/negotiationLabels";
import { useLang, translateFr as tfr } from "@/i18n";

export function IdentityStep({ draft, update, player, config }) {
  const { tf } = useLang();
  const [filter, setFilter] = useState("attention");
  const sd = player?.sport_data || {};
  const baseValue = (id) => player?.[id] ?? sd[id] ?? "";
  const value = (id) => draft.identity?.[id] ?? baseValue(id) ?? "";
  const statusOf = (id) => {
    if (draft.identity?.[id]) return "modified";
    if (baseValue(id)) return "athlynx";
    return "missing";
  };
  const fields = useMemo(() => {
    const all = config?.identity_fields || [];
    if (filter === "all") return all;
    if (filter === "missing") return all.filter((f) => statusOf(f.id) === "missing");
    return all.filter((f) => statusOf(f.id) !== "athlynx");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, filter, draft.identity, player]);

  return (
    <div data-testid="ws-step-identity">
      <p className="text-xs text-muted-foreground mb-4">{t("negotiationProfile.identity.hint")}</p>
      <div className="mb-5 max-w-xs">
        <Label className="text-xs mb-1.5 block">{t("negotiationProfile.identity.show")}</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9" data-testid="identity-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="attention">{t("negotiationProfile.identity.filter.attention")}</SelectItem>
            <SelectItem value="missing">{t("negotiationProfile.identity.filter.missing")}</SelectItem>
            <SelectItem value="all">{t("negotiationProfile.identity.filter.all")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground rounded-xl border border-border bg-secondary/30 p-4" data-testid="identity-all-good">
          {tfr("Toutes les informations d'identité sont confirmées depuis le profil Athlynx. ✓")}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.id} className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`id-${f.id}`} className="text-xs">{tf(f.label)}</Label>
                <FieldStatusChip status={statusOf(f.id)} />
              </div>
              <Input
                id={`id-${f.id}`}
                type={f.type === "date" ? "date" : "text"}
                value={value(f.id)}
                onChange={(e) => update(["identity", f.id], e.target.value)}
                placeholder="—"
                className="h-10"
                data-testid={`identity-input-${f.id}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function StatsStep({ draft, update, config }) {
  const { tf } = useLang();
  const stats = draft.stats || {};
  const selected = stats.selected || [];
  const toggle = (key) => {
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : selected.length < 6 ? [...selected, key] : selected;
    update(["stats", "selected"], next);
  };
  return (
    <div data-testid="ws-step-stats">
      <p className="text-xs text-muted-foreground mb-4">{t("negotiationProfile.stats.hint")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5 max-w-lg">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("negotiationProfile.stats.season")}</Label>
          <Select value={stats.season_label || "Saison 2025/2026"} onValueChange={(v) => update(["stats", "season_label"], v)}>
            <SelectTrigger className="h-9" data-testid="stats-season-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Saison 2024/2025", "Saison 2025/2026", "Saison 2026/2027"].map((s) => (
                <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("negotiationProfile.stats.source")}</Label>
          <Select value={stats.source || "manual"} onValueChange={(v) => update(["stats", "source"], v)}>
            <SelectTrigger className="h-9" data-testid="stats-source-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["athlynx", "Athlynx"], ["manual", "Saisie manuelle"], ["statsbomb", "StatsBomb"],
                ["wyscout", "Wyscout"], ["csv", "Import CSV"], ["excel", "Import Excel"]].map(([id, label]) => (
                <SelectItem key={id} value={id}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <WsSection
        title={t("negotiationProfile.stats.selected")}
        right={<span className="text-xs font-semibold tabular-nums text-muted-foreground" data-testid="stats-selected-count">{selected.length} / 6</span>}
      >
        <div className="space-y-2.5">
          {(config?.metrics || []).map((m) => {
            const on = selected.includes(m.key);
            return (
              <div key={m.key} className="flex items-center gap-3 rounded-xl border border-border bg-secondary/20 px-3 py-2" data-testid={`stat-row-${m.key}`}>
                <input
                  type="checkbox"
                  id={`sel-${m.key}`}
                  checked={on}
                  onChange={() => toggle(m.key)}
                  disabled={!on && selected.length >= 6}
                  className="h-4 w-4 accent-[#E5201A] shrink-0 cursor-pointer"
                  aria-label={`Afficher ${m.label} sur la fiche`}
                  data-testid={`stat-select-${m.key}`}
                />
                <label htmlFor={`sel-${m.key}`} className="text-sm flex-1 min-w-0 truncate cursor-pointer">{tf(m.label)}</label>
                <Input
                  value={stats.values?.[m.key] ?? ""}
                  onChange={(e) => update(["stats", "values", m.key], e.target.value)}
                  placeholder="—"
                  className="h-9 w-28 text-right"
                  aria-label={m.label}
                  data-testid={`stat-input-${m.key}`}
                />
              </div>
            );
          })}
        </div>
      </WsSection>
    </div>
  );
}
